/**
 * Guided Flow Controller
 *
 * Orchestrates guided experiences through the application.
 * Uses provider APIs for all external interactions.
 */

import {
  GuidedFlowDefinition,
  FlowContext,
  FlowStep,
  FlowControllerAPI,
  FlowEvent,
  FlowEventHandler,
  FlowEventType,
} from './types';
import { FlowProviders } from '../providers/types';

export class GuidedFlowController implements FlowControllerAPI {
  private flow: GuidedFlowDefinition | null = null;
  private context: FlowContext | null = null;
  private listeners: Map<FlowEventType, Set<FlowEventHandler>> = new Map();
  private isPaused: boolean = false;
  private stepTimeout: NodeJS.Timeout | null = null;
  private providers: FlowProviders;

  constructor(providers: FlowProviders) {
    this.providers = providers;
  }

  // ============================================================================
  // Flow Management
  // ============================================================================

  async start(flowId: string, initialContext?: Partial<FlowContext>): Promise<void> {
    // Load flow from registry
    const flow = await this.loadFlow(flowId);

    if (!flow) {
      throw new Error(`Flow not found: ${flowId}`);
    }

    this.flow = flow;

    // Initialize context
    this.context = {
      flowId: flow.id,
      flowName: flow.name,
      currentStepId: flow.startStepId,
      currentStepIndex: 0,
      totalSteps: flow.steps.length,
      data: {},
      completedSteps: [],
      visitedSteps: [],
      startTime: new Date(),
      stepStartTime: new Date(),
      mode: 'demo',
      isDemo: true,
      autoPlay: flow.config?.autoPlay ?? false,
      speed: flow.config?.defaultSpeed ?? 'normal',
      ...initialContext,
    };

    // Track flow start
    this.providers.analytics.track('flow:start', {
      flowId: flow.id,
      flowName: flow.name,
      mode: this.context.mode,
    });

    // Emit start event
    this.emit('flow:start', { flowId: flow.id });

    // Call lifecycle hook
    if (flow.onStart) {
      await flow.onStart(this.context);
    }

    // Start first step
    await this.executeStep(flow.startStepId);
  }

  pause(): void {
    this.isPaused = true;
    if (this.stepTimeout) {
      clearTimeout(this.stepTimeout);
      this.stepTimeout = null;
    }
  }

  resume(): void {
    this.isPaused = false;
    const currentStep = this.getCurrentStep();
    if (currentStep?.autoAdvance && this.context) {
      this.scheduleAutoAdvance(currentStep);
    }
  }

  cancel(): void {
    if (this.flow && this.context) {
      this.providers.analytics.track('flow:cancel', {
        flowId: this.flow.id,
        lastStep: this.context.currentStepId,
        progress: (this.context.completedSteps.length / this.context.totalSteps) * 100,
      });

      this.emit('flow:cancel', { flowId: this.flow.id });

      if (this.flow.onCancel) {
        this.flow.onCancel(this.context);
      }
    }

    this.cleanup();
  }

  async restart(): Promise<void> {
    const flowId = this.flow?.id;
    const user = this.context?.user;

    this.cleanup();

    if (flowId) {
      await this.start(flowId, { user });
    }
  }

  // ============================================================================
  // Navigation
  // ============================================================================

  async next(): Promise<void> {
    if (!this.flow || !this.context) {
      throw new Error('No active flow');
    }

    const currentStep = this.getCurrentStep();
    if (!currentStep) {
      throw new Error('Current step not found');
    }

    // Mark current step as completed
    if (!this.context.completedSteps.includes(currentStep.id)) {
      this.context.completedSteps.push(currentStep.id);
    }

    // Track step completion
    this.providers.analytics.track('flow:step_complete', {
      flowId: this.flow.id,
      stepId: currentStep.id,
      timeSpent: Date.now() - this.context.stepStartTime.getTime(),
    });

    // Emit step complete event
    this.emit('step:complete', {
      flowId: this.flow.id,
      stepId: currentStep.id,
    });

    // Call step completion hook
    if (currentStep.onComplete) {
      await currentStep.onComplete(this.context);
    }

    // Determine next step
    const nextStepId = this.getNextStepId(currentStep);

    if (!nextStepId) {
      // Flow complete
      await this.completeFlow();
      return;
    }

    // Execute next step
    await this.executeStep(nextStepId);
  }

  async previous(): Promise<void> {
    if (!this.flow || !this.context) {
      throw new Error('No active flow');
    }

    const currentIndex = this.context.currentStepIndex;
    if (currentIndex <= 0) {
      return; // Already at first step
    }

    const previousStep = this.flow.steps[currentIndex - 1];
    if (previousStep) {
      await this.executeStep(previousStep.id);
    }
  }

  async goToStep(stepId: string): Promise<void> {
    if (!this.flow) {
      throw new Error('No active flow');
    }

    const step = this.flow.steps.find(s => s.id === stepId);
    if (!step) {
      throw new Error(`Step not found: ${stepId}`);
    }

    await this.executeStep(stepId);
  }

  skip(): void {
    const currentStep = this.getCurrentStep();
    if (currentStep?.skippable) {
      this.providers.analytics.track('flow:step_skipped', {
        flowId: this.flow!.id,
        stepId: currentStep.id,
      });

      this.emit('step:skip', {
        flowId: this.flow!.id,
        stepId: currentStep.id,
      });

      this.next();
    }
  }

  // ============================================================================
  // State Management
  // ============================================================================

  getContext(): FlowContext {
    if (!this.context) {
      throw new Error('No active flow context');
    }
    return { ...this.context };
  }

  updateContext(data: Partial<FlowContext>): void {
    if (!this.context) {
      throw new Error('No active flow context');
    }
    this.context = { ...this.context, ...data };
  }

  getCurrentStep(): FlowStep | null {
    if (!this.flow || !this.context) {
      return null;
    }
    return this.flow.steps.find(s => s.id === this.context!.currentStepId) || null;
  }

  setData(key: string, value: any): void {
    if (!this.context) {
      throw new Error('No active flow context');
    }
    this.context.data[key] = value;
  }

  getData(key: string): any {
    if (!this.context) {
      throw new Error('No active flow context');
    }
    return this.context.data[key];
  }

  // ============================================================================
  // Control
  // ============================================================================

  setSpeed(speed: 'slow' | 'normal' | 'fast'): void {
    if (this.context) {
      this.context.speed = speed;
    }
  }

  setAutoPlay(enabled: boolean): void {
    if (this.context) {
      this.context.autoPlay = enabled;

      if (enabled && !this.isPaused) {
        const currentStep = this.getCurrentStep();
        if (currentStep?.autoAdvance) {
          this.scheduleAutoAdvance(currentStep);
        }
      }
    }
  }

  // ============================================================================
  // Provider Access (for step handlers)
  // ============================================================================

  getProviders(): FlowProviders {
    return this.providers;
  }

  // ============================================================================
  // Events
  // ============================================================================

  on(event: FlowEventType, handler: FlowEventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: FlowEventType, handler: FlowEventHandler): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private emit(type: FlowEventType, data?: any): void {
    const event: FlowEvent = {
      type,
      timestamp: new Date(),
      flowId: this.flow?.id || '',
      data,
    };

    const handlers = this.listeners.get(type);
    if (handlers) {
      handlers.forEach(handler => handler(event));
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private async executeStep(stepId: string): Promise<void> {
    if (!this.flow || !this.context) {
      throw new Error('No active flow');
    }

    // Clear any pending auto-advance
    if (this.stepTimeout) {
      clearTimeout(this.stepTimeout);
      this.stepTimeout = null;
    }

    // Find step
    const step = this.flow.steps.find(s => s.id === stepId);
    if (!step) {
      throw new Error(`Step not found: ${stepId}`);
    }

    // Update context
    const stepIndex = this.flow.steps.indexOf(step);
    this.context.currentStepId = stepId;
    this.context.currentStepIndex = stepIndex;
    this.context.stepStartTime = new Date();

    if (!this.context.visitedSteps.includes(stepId)) {
      this.context.visitedSteps.push(stepId);
    }

    // Track analytics
    this.providers.analytics.track('flow:step_start', {
      flowId: this.flow.id,
      stepId: step.id,
      stepType: step.type,
    });

    // Emit step start event
    this.emit('step:start', {
      flowId: this.flow.id,
      stepId: step.id,
    });

    // Call flow step change hook
    if (this.flow.onStepChange) {
      await this.flow.onStepChange(this.context, step);
    }

    // Handle auto-advance
    if (step.autoAdvance && this.context.autoPlay && !this.isPaused) {
      this.scheduleAutoAdvance(step);
    }
  }

  private scheduleAutoAdvance(step: FlowStep): void {
    if (this.stepTimeout) {
      clearTimeout(this.stepTimeout);
    }

    const delay = this.calculateDelay(step.autoAdvanceDelay || 2000);

    this.stepTimeout = setTimeout(() => {
      this.next();
    }, delay);
  }

  private calculateDelay(baseDelay: number): number {
    if (!this.context) return baseDelay;

    const speedMultipliers = {
      slow: 1.5,
      normal: 1.0,
      fast: 0.5,
    };

    return baseDelay * speedMultipliers[this.context.speed || 'normal'];
  }

  private getNextStepId(currentStep: FlowStep): string | null {
    if (!this.context) return null;

    if (typeof currentStep.nextStep === 'function') {
      return currentStep.nextStep(this.context);
    }

    return currentStep.nextStep || null;
  }

  private async completeFlow(): Promise<void> {
    if (!this.flow || !this.context) return;

    const duration = Date.now() - this.context.startTime.getTime();

    this.providers.analytics.track('flow:complete', {
      flowId: this.flow.id,
      duration,
      stepsCompleted: this.context.completedSteps.length,
    });

    this.emit('flow:complete', {
      flowId: this.flow.id,
      duration,
    });

    if (this.flow.onComplete) {
      await this.flow.onComplete(this.context);
    }

    this.cleanup();
  }

  private cleanup(): void {
    if (this.stepTimeout) {
      clearTimeout(this.stepTimeout);
      this.stepTimeout = null;
    }

    this.flow = null;
    this.context = null;
    this.isPaused = false;
  }

  private async loadFlow(flowId: string): Promise<GuidedFlowDefinition | null> {
    // TO BE IMPLEMENTED: Load flow from registry
    throw new Error('Flow loading not implemented - use FlowRegistry.registerFlow() first');
  }
}

// ============================================================================
// Flow Registry (Singleton)
// ============================================================================

class FlowRegistry {
  private flows: Map<string, GuidedFlowDefinition> = new Map();

  registerFlow(flow: GuidedFlowDefinition): void {
    this.flows.set(flow.id, flow);
  }

  getFlow(flowId: string): GuidedFlowDefinition | null {
    return this.flows.get(flowId) || null;
  }

  getAllFlows(): GuidedFlowDefinition[] {
    return Array.from(this.flows.values());
  }

  unregisterFlow(flowId: string): void {
    this.flows.delete(flowId);
  }
}

export const flowRegistry = new FlowRegistry();

// Patch the controller to use the registry
GuidedFlowController.prototype['loadFlow'] = async function(flowId: string) {
  return flowRegistry.getFlow(flowId);
};
